/**
 * StudentActivityBody — ce que l'exécution d'une activité montre, selon où elle en est : la page
 * d'activité (et ses boîtes, l'enregistreur), la correction, les félicitations. Elle pose ce que
 * `useStudentActivityView` a préparé, et dit la fin à son appelant (`onFinished`) : l'écran d'une
 * route navigue, l'écran du live reprend la main.
 *
 * Les félicitations reprennent l'ancien écran de fin (Wajih, 18/10) : le jingle, les confettis, les
 * gains qui défilent, puis la suite au geste de l'élève, ou au bout du compte à rebours de 12 s que la
 * page tient. Le retour d'Android n'y fait rien, comme avant, et `congrats.activity_finalized` part
 * vers Amplitude à l'ouverture.
 *
 * Le quiz de prérequis pas encore passé (`<StudentPrerequisiteQuiz>`) prend la place de l'activité,
 * comme l'ancien : l'énoncé ne se lit pas avant lui. Sa correction (`<StudentQuizReview>`) aussi, à
 * la fin du quiz puis par « Revoir le quiz ».
 *
 * Avant l'énoncé, l'entrée de l'ancien `TimerActivityStep` (`<ActivityStartCountdown>`) : « Ton
 * activité va commencer... » puis 3, 2, 1, ou « Ton activité va reprendre... » ; le chrono attend sa
 * fin, ou « Passer l'animation ».
 *
 * D'une étape à l'autre (l'activité, la correction, les félicitations, le quiz, sa relecture), la page
 * qui part sort et la suivante entre (`<StepStage>`, `entree`), au lieu de couper sec : l'étape est une
 * règle pure (`activityStage`).
 *
 * Deux choses flottent au coin, qui ne se confondent pas :
 *  - `tandem`, la bulle tandem du live, que seul le live remplit : elle dit le mode, donc elle reste à
 *    chaque étape (Wajih, 20/10). Elle est posée UNE fois, au-dessus des étapes (`<FloatingSlot>`) :
 *    d'une étape à l'autre, elle n'est ni démontée ni remontée, sa vidéo non plus. Les pages ne
 *    reçoivent que la place à lui garder. Une activité déjà rendue ne la montre pas, comme avant, sauf
 *    celle que l'élève vient de soumettre en Dialogue (il y reste, son masteur lui parle) ;
 *  - `samTile`, la tuile de Sam hors live, qui ouvre sa conversation (`samLayer`) : elle n'a de sens
 *    que sur la page d'activité, la seule qui rend cette conversation. Sur le quiz de prérequis et sa
 *    relecture, la toucher n'ouvrait rien.
 */
import React, { useEffect, useRef, useState } from "react"
import { BackHandler } from "react-native"

import { ConfirmDialog, FloatingSlot, StepStage } from "../../../../../../design-system"
import {
    ActivityStartCountdown,
    EnonceReadingSheet,
    StudentActivityCongratsPage,
    StudentActivityPage,
} from "../../../../../../design-system/student"
import { trackCongratsShown } from "./activityAnalytics"
import { activityStageKey, activityStageOf, type ActivityStage } from "./activityStage"
import { playStudentSound } from "../studentSounds"
import { ACTIVITY_SOUNDS } from "./activitySounds"
import { StudentActivityCorrection } from "./StudentActivityCorrection"
import { StudentFinishedActivity } from "./StudentFinishedActivity"
import { StudentPrerequisiteQuiz } from "./StudentPrerequisiteQuiz"
import { StudentQuizReview } from "./StudentQuizReview"
import { StudentActivityRecorder } from "./StudentActivityRecorder"
import { StudentCopyCamera } from "./StudentCopyCamera"
import { useChronoAlertSounds } from "./useChronoAlertSounds"
import { useEnonceReading } from "./useEnonceReading"
import { type StudentActivityView } from "./useStudentActivityView"
import { type ActivityCongratsNextKind } from "../../../../view-models/student/Activity/studentActivityCongratsViewModel"

/** Comment l'activité s'est terminée, et par quoi enchaîner. */
export type StudentActivityOutcome =
    /** Rendue (ou déjà rendue) : l'activité suivante, ou la liste. */
    | { kind: "done"; next: Exclude<ActivityCongratsNextKind, "activity"> }
    /** Soumise en Dialogue : l'élève reste dessus, elle se relit ; rien ne s'ouvre à sa place. */
    | { kind: "submitted" }
    /**
     * « Retour à mes activités », depuis les félicitations. Pas une fin : il peut revenir (dans le live, la liste
     * s'ouvre par-dessus les félicitations), et un `done` peut le suivre. Les autres se disent une fois.
     */
    | { kind: "stopped" }
    /** Sortie sans rendre. */
    | { kind: "left" }
    /** L'activité n'existe plus. */
    | { kind: "deleted" }

export type StudentActivityBodyProps = {
    view: StudentActivityView
    onFinished: (outcome: StudentActivityOutcome) => void
    /** La bulle tandem du live, au coin de chaque étape. Seul le live la passe. */
    tandem?: React.ReactNode
    /** La tuile de Sam hors live, sur la page d'activité seule (sa conversation est `samLayer`). */
    samTile?: React.ReactNode
    samLayer?: React.ReactNode
    onFooterHeight?: (height: number) => void
    /**
     * La colonne du live au grand écran (`StudentActivityPage column`) : celle du volet, ou celle de
     * lecture quand la visio est refermée.
     */
    column?: number
    /**
     * L'activité se voit. `false` (le live au téléphone, l'activité posée sous la visio) : son entrée et
     * ses félicitations attendent qu'elle s'ouvre — ni décompte ni son joués sans être vus, et le chrono
     * ne part qu'avec le décompte. Défaut `true`.
     */
    onScreen?: boolean
    /**
     * Le compte à rebours des félicitations attend : dans le live, la liste des activités est ouverte par-dessus.
     * Il repart de zéro quand elle se referme (Wajih, 27/10). Défaut `false`.
     */
    countdownHeld?: boolean
}

/** L'entrée d'une activité : le son du départ avec le décompte d'une activité neuve, coupé à la fin. */
function Intro({
    intro,
    title,
    inLive,
}: {
    intro: NonNullable<StudentActivityView["intro"]>
    title: string
    inLive: boolean
}) {
    // Le son part avec le décompte, pas avec l'annonce : ses bips sont calés sur le dessin, comme
    // l'ancien `TimerActivityStep` qui le lançait avec l'animation.
    const stopRef = useRef<() => void>(() => undefined)
    useEffect(() => () => stopRef.current(), [])
    return (
        <ActivityStartCountdown
            kind={intro.kind}
            title={title}
            onCountStart={() => {
                if (intro.kind === "start") stopRef.current = playStudentSound(ACTIVITY_SOUNDS.start, inLive, "start")
            }}
            onDone={() => {
                stopRef.current()
                intro.onDone()
            }}
        />
    )
}

function Congrats({
    model,
    inLive,
    onContinue,
    onStop,
    countdownHeld,
}: {
    model: NonNullable<StudentActivityView["congrats"]>
    inLive: boolean
    onContinue: () => void
    onStop: () => void
    countdownHeld: boolean
}) {
    // À l'ouverture, comme l'ancien écran de fin : le jingle, la mesure, le retour d'Android bloqué.
    useEffect(() => {
        playStudentSound(ACTIVITY_SOUNDS.finished, inLive, "finished")
        trackCongratsShown()
        const subscription = BackHandler.addEventListener("hardwareBackPress", () => true)
        return () => subscription.remove()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])
    return (
        <StudentActivityCongratsPage
            champion={model.champion}
            title={model.title}
            subtitle={model.subtitle}
            xp={model.xp}
            petals={model.petals}
            durationSeconds={model.durationSeconds}
            next={model.next}
            onContinue={onContinue}
            onBackToActivities={onStop}
            countdownPaused={countdownHeld}
        />
    )
}

export function StudentActivityBody(props: StudentActivityBodyProps) {
    const { view, onFinished } = props
    const { run } = view
    const phase = run.state.phase

    // La fin se dit une fois.
    const told = useRef<string | null>(null)
    const finish = (outcome: StudentActivityOutcome, key: string) => {
        if (told.current === key) return
        told.current = key
        onFinished(outcome)
    }
    useEffect(() => {
        told.current = null
    }, [run.activityId])
    useEffect(() => {
        if (phase === "done") finish({ kind: "done", next: view.nextActivity != null ? "next" : "activities" }, "done")
        // Soumise en Dialogue, après les félicitations (ou « déjà soumise », sans elles) : elle se relit.
        if (phase === "submitted") finish({ kind: "submitted" }, "submitted")
        if (phase === "left") finish({ kind: "left" }, "left")
        // L'activité retirée : on passe à la suite au bout de 5 s, comme `ActivityDeletedModal`.
        if (phase !== "deleted") return
        const timer = setTimeout(() => finish({ kind: "deleted" }, "deleted"), DELETED_AUTO_CONTINUE_MS)
        return () => clearTimeout(timer)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [phase])

    // Une étape à la fois : d'une étape à l'autre, la page sort et la suivante entre, au lieu de couper
    // sec. La bulle tandem du live reste au-dessus des étapes, la même d'un bout à l'autre.
    const stage = activityStageOf(view)
    const stepKey = activityStageKey(stage, view.finished?.id ?? run.activityId)
    // Ce qui se joue pour être vu (l'entrée, les félicitations) attend que l'activité s'ouvre ; vu une fois,
    // il reste posé même caché : ni le décompte ni le jingle ne rejouent. Relu pendant le rendu (l'état
    // dérivé de React).
    const onScreen = props.onScreen !== false
    const [shownStep, setShownStep] = useState<string | null>(null)
    if (onScreen && shownStep !== stepKey) setShownStep(stepKey)
    const held = !onScreen && shownStep !== stepKey && (stage === "intro" || stage === "congrats")
    const tandem = stage === "finished" && phase !== "submitted" ? undefined : props.tandem
    return (
        <FloatingSlot floating={tandem}>
            <StepStage stepKey={stepKey} enter="entree" fill>
                {held ? null : <Stage {...props} stage={stage} finish={finish} />}
            </StepStage>
        </FloatingSlot>
    )
}

function Stage(
    props: StudentActivityBodyProps & {
        stage: ActivityStage
        finish: (outcome: StudentActivityOutcome, key: string) => void
    }
) {
    const { view, stage, finish } = props
    const { run } = view

    // Une activité déjà rendue, que le tuteur affiche en live : elle se relit (sa copie, sa correction).
    if (stage === "finished" && view.finished != null) {
        return (
            <StudentFinishedActivity
                activity={view.finished}
                inline={view.heading === "inline"}
                onReplayed={view.onReplayed}
            />
        )
    }

    if (stage === "correction" && view.correction != null) {
        return (
            <StudentActivityCorrection
                activity={view.correction.activity}
                questions={view.correction.questions}
                onDone={run.correctionDone}
                onLeftUnsettled={view.correction.onLeftUnsettled}
            />
        )
    }

    if (stage === "congrats" && view.congrats != null) {
        const model = view.congrats
        const next = model.next
        // Soumise en Dialogue : la suite est l'activité elle-même, rendue, qui se relit (la phase le dit
        // à l'appelant) ; la page n'y montre pas « Retour à mes activités » (Wajih, 26/10).
        const stays = run.state.stays
        return (
            <Congrats
                model={model}
                inLive={view.inLive}
                onContinue={() => {
                    if (stays) run.backToActivity()
                    else if (next !== "activity") finish({ kind: "done", next }, "done")
                }}
                // « Retour à mes activités » n'est pas la fin : dans le live, la liste s'ouvre par-dessus les
                // félicitations, et l'élève qui la referme sans rien choisir y revient. La suite doit encore
                // partir, au geste comme au bout du compte à rebours (Wajih, 26/10).
                onStop={() => props.onFinished({ kind: "stopped" })}
                countdownHeld={props.countdownHeld === true}
            />
        )
    }

    // Le quiz de prérequis pas encore passé prend la place de l'énoncé, comme l'ancien : il ne se lit
    // pas avant le quiz, au téléphone comme sur un grand écran. Puis sa correction (à la fin du quiz,
    // ou « Revoir le quiz ») prend la même place.
    if (stage === "quiz") {
        return (
            <StudentPrerequisiteQuiz
                gate={view.quiz}
                title={view.page.title}
                heading={view.heading}
                onLeave={view.page.onBack}
                inLive={view.inLive}
            />
        )
    }
    if (stage === "review") return <StudentQuizReview review={view.quiz.review} />
    if (stage === "intro" && view.intro != null) {
        return <Intro intro={view.intro} title={view.page.title} inLive={view.inLive} />
    }

    return <WorkingPage {...props} />
}

/** La page d'activité, sa lecture (« Ma lecture ») et ses boîtes. */
function WorkingPage(
    props: StudentActivityBodyProps & { finish: (outcome: StudentActivityOutcome, key: string) => void }
) {
    const { view, finish, samTile, samLayer, onFooterHeight, column } = props
    const reading = useEnonceReading(view.statementKey)
    useChronoAlertSounds(view.page.chrono, view.inLive)
    const dialog = view.dialog
    const deleted = view.deletedDialog
    return (
        <>
            <StudentActivityPage
                {...view.page}
                statementReading={reading.statement ?? undefined}
                column={column}
                samTile={samTile}
                samLayer={samLayer}
                onFooterHeight={onFooterHeight}
            />
            {reading.sheet != null && <EnonceReadingSheet {...reading.sheet} />}
            {view.recorder != null && <StudentActivityRecorder {...view.recorder} />}
            {view.camera != null && <StudentCopyCamera {...view.camera} />}
            <ConfirmDialog
                visible={dialog != null}
                title={dialog?.title ?? ""}
                message={dialog?.message}
                confirmLabel={dialog?.confirmLabel}
                cancelLabel={dialog?.cancelLabel}
                hideCancel={dialog?.single}
                danger={false}
                onConfirm={() => dialog?.onConfirm()}
                onCancel={() => dialog?.onCancel()}
                onDismiss={() => dialog?.onDismiss()}
            />
            <ConfirmDialog
                visible={deleted != null}
                title={deleted?.title ?? ""}
                message={deleted?.message}
                confirmLabel={deleted?.confirmLabel}
                hideCancel
                danger={false}
                onConfirm={() => finish({ kind: "deleted" }, "deleted")}
                onCancel={() => finish({ kind: "deleted" }, "deleted")}
            />
        </>
    )
}

const DELETED_AUTO_CONTINUE_MS = 5000
